/**
 * OwnerCell - the lead's recruiter ON THIS STUDY, changed in the row (docs/plans/crm-recruitment-
 * list-plan.md Part A section 4, B4; per study since migration 20260929190000): the inbox's compact
 * OwnerSelect, writing PATCH .../studies/:studyId/owner: on the lead's focus study the server moves
 * their main recruiter, on any other study only the row's own, so handing a returning lead to this
 * study's recruiter leaves them with their recruiter on the other study.
 *
 * OwnerSelect reads its value once, when it mounts. The row can change owner without this cell
 * (Set recruiter in the bulk bar, a teammate, the minute's refresh), so the select is keyed by the
 * owner and re-mounts on an outside change; its own change lands first and is then adopted.
 */
import { useCallback, useEffect, useRef } from 'react';
import OwnerSelect from '../../../components/OwnerSelect';
import { rowOwnerId, withRowOwner } from '../../recruitLogic';
import type { RecruitRow, RecruitRowChangeHandler } from '../../types';

export interface OwnerCellProps {
  row: RecruitRow;
  onRowChange: RecruitRowChangeHandler;
  /** Sheet keyboard: Enter opened this cell (focus the select). */
  editing?: boolean;
  /** The edit finished (a change landed). */
  onEditEnd?: () => void;
  className?: string;
}

export function OwnerCell({ row, onRowChange, editing = false, onEditEnd, className = '' }: OwnerCellProps) {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const contactId = row.contact.id;
  const owner = rowOwnerId(row);
  const studyId = row.membership.study_id;
  const onEditEndRef = useRef(onEditEnd);
  onEditEndRef.current = onEditEnd;

  useEffect(() => {
    if (editing) wrapRef.current?.querySelector('select')?.focus();
  }, [editing]);

  const onChanged = useCallback((next: string | null) => {
    onRowChange(contactId, (r) => withRowOwner(r, next));
    onEditEndRef.current?.();
  }, [contactId, onRowChange]);

  return (
    <span ref={wrapRef} className={`block min-w-0 max-w-full ${className}`}>
      <OwnerSelect
        key={owner || 'unassigned'}
        contactId={contactId}
        value={owner}
        studyId={studyId}
        compact
        onChanged={onChanged}
      />
    </span>
  );
}

export default OwnerCell;
